import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import {
  AdminAppVersionsDocument,
  AdminOrganizationsDocument,
  AdministratorSelfDocument,
} from '@/graphql/generated'
import { graphqlRequest } from '@/lib/api/graphql'
import { keys } from '@/lib/queries/keys'
import { fullName, nodes } from '@/lib/queries/mappers'

/**
 * Platform administration.
 *
 * No privileged endpoint is involved — see the notes in `documents/admin.gql`.
 * RLS is what makes these safe to call from anyone's browser: a non-admin gets
 * an empty administrators row set and only their own organizations back.
 */

/**
 * Is the signed-in user a platform administrator?
 *
 * Kept long-lived: the answer changes when someone edits the `administrators`
 * table, which is a support operation, not something the UI does. Gates the
 * sidebar's Platform section; the /admin screen itself needs no guard because
 * RLS already returns a non-admin nothing worth guarding.
 */
export function isAdministratorQuery(userId: string) {
  return queryOptions({
    queryKey: keys.administratorSelf(userId),
    queryFn: ({ signal }) =>
      graphqlRequest(AdministratorSelfDocument, { userId }, { signal }),
    staleTime: 15 * 60_000,
    select: (data): boolean => nodes(data.administratorsCollection).length > 0,
  })
}

export type AdminOrganization = {
  id: string
  slug: string
  name: string
  /** `organization_types.name` — the same "plan" stand-in the switcher shows. */
  plan: string
  memberCount: number
  codeCount: number
  createdAt: string
}

/** Rows per request. Enough to fill a tall window, small enough to feel instant. */
export const ADMIN_ORGANIZATIONS_PAGE_SIZE = 50

/**
 * A search term as an ILIKE pattern.
 *
 * `%` (match everything) when the box is empty, so the document always has a
 * pattern to apply and needs no conditional filter. `%` and `_` are escaped
 * because they are wildcards: an admin pasting a slug fragment containing `_`
 * should match that underscore, not any character.
 */
export function organizationSearchPattern(term: string): string {
  const trimmed = term.trim()
  if (!trimmed) return '%'
  return `%${trimmed.replace(/[\\%_]/g, (c) => `\\${c}`)}%`
}

/**
 * The administrators' directory, one page at a time.
 *
 * Infinite rather than one big fetch: every row drags two nested collections
 * behind it purely to count them, so "all of them" is thousands of rows for a
 * screen that shows a few dozen. Pages are keyed by the search term, so typing
 * starts a new list rather than filtering the pages already loaded — the
 * filtering happens in Postgres, which is the only place that can see the
 * organizations you haven't scrolled to yet.
 */
export function adminOrganizationsQuery(term = '') {
  const pattern = organizationSearchPattern(term)

  return infiniteQueryOptions({
    queryKey: keys.adminOrganizations(pattern),
    queryFn: ({ pageParam, signal }) =>
      graphqlRequest(
        AdminOrganizationsDocument,
        { first: ADMIN_ORGANIZATIONS_PAGE_SIZE, after: pageParam, pattern },
        { signal },
      ),
    initialPageParam: null as string | null,
    /* `undefined` means "no more pages" — React Query reads it to set
       `hasNextPage`, which is what stops the scroll sentinel asking again. */
    getNextPageParam: (last) =>
      last.organizationsCollection?.pageInfo.hasNextPage
        ? (last.organizationsCollection.pageInfo.endCursor ?? undefined)
        : undefined,
    select: (data): AdminOrganization[] =>
      data.pages.flatMap((page) =>
        nodes(page.organizationsCollection).map((node) => ({
          id: node.id,
          slug: node.slug,
          name: node.name,
          plan: node.organizationType?.name ?? 'Personal Account',
          memberCount: nodes(node.organizationMembersCollection).length,
          codeCount: nodes(node.constellationsCollection).length,
          createdAt: node.createdAt,
        })),
      ),
  })
}

/* ---------------------------------------------------------------------------
 * App versions
 * ------------------------------------------------------------------------ */

/** One person, and the build they were last seen running. */
export type AppVersionUser = {
  id: string
  name: string
  email: string
  /** `<semver>+<sha>`, exactly as the app reported it. */
  version: string
  /** ISO 8601, or null for a row written before the timestamp existed. */
  seenAt: string | null
}

/** Everyone on one build. */
export type AppVersionGroup = {
  version: string
  users: AppVersionUser[]
  /** The most recent sighting of anyone on this build. */
  lastSeenAt: string | null
}

/**
 * Rows per request.
 *
 * Larger than the organizations directory's page because each row here is six
 * scalar columns with nothing nested, and because this screen needs the WHOLE
 * set to be correct — a count of "38 users on 0.2.0" computed from a partial
 * fetch is not a smaller truth, it is a wrong number.
 */
export const ADMIN_APP_VERSIONS_PAGE_SIZE = 200

/**
 * The most pages this screen will pull before it stops and says so.
 *
 * The grouping is client-side, so the honest version of this screen fetches
 * everyone who has ever opened the app. That is fine at the platform's current
 * size and would not be at ten times it, so the ceiling exists to bound the
 * damage rather than to be hit. Rows come back most-recently-seen first, so if
 * it ever IS hit, what survives is the half anyone would have looked at.
 *
 * `truncated` is plumbed all the way to the screen — a silently capped count
 * would read as a complete one, which is the specific failure this ceiling
 * would otherwise cause.
 */
export const ADMIN_APP_VERSIONS_MAX_PAGES = 25

/**
 * Everyone who has opened the gen4 app, and which build they were running.
 *
 * No admin gate, for the same reason as the organizations directory: RLS
 * answers this with only the users the caller may read, so a non-administrator
 * sees exactly one row — their own — and learns nothing. The sidebar hides the
 * link; the data hides itself.
 */
export function adminAppVersionsQuery() {
  return infiniteQueryOptions({
    queryKey: keys.adminAppVersions(),
    queryFn: ({ pageParam, signal }) =>
      graphqlRequest(
        AdminAppVersionsDocument,
        { first: ADMIN_APP_VERSIONS_PAGE_SIZE, after: pageParam },
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last, allPages) => {
      if (allPages.length >= ADMIN_APP_VERSIONS_MAX_PAGES) return undefined
      return last.usersCollection?.pageInfo.hasNextPage
        ? (last.usersCollection.pageInfo.endCursor ?? undefined)
        : undefined
    },
    /* A version report is a background write that can land at any moment, and
       nothing here is worth a refetch storm — the screen is read on purpose,
       not watched. */
    staleTime: 60_000,
    select: (data) => ({
      users: data.pages.flatMap((page) =>
        nodes(page.usersCollection).map(
          (node): AppVersionUser => ({
            id: node.id,
            name: fullName(node),
            email: node.emailAddress ?? '',
            /* The filter excludes NULL versions, so this coalesce is only
               reachable if the filter is ever loosened. Grouping under an empty
               string would silently merge those rows into one nameless bucket,
               which is worse than an obvious label. */
            version: node.appVersion ?? 'unknown',
            seenAt: node.appVersionSeenAt,
          }),
        ),
      ),
      /* True only when the CEILING stopped us — a natural end of the list is
         not truncation. `hasNextPage` on the final page distinguishes them. */
      truncated:
        data.pages.length >= ADMIN_APP_VERSIONS_MAX_PAGES &&
        Boolean(data.pages.at(-1)?.usersCollection?.pageInfo.hasNextPage),
    }),
  })
}

/**
 * Users -> one bucket per build, biggest first.
 *
 * Grouped here rather than in SQL because the screen needs both the counts and
 * the members of each bucket, and pg_graphql has no aggregate that would return
 * them together — two round trips to avoid one `reduce` is a poor trade.
 *
 * Ordered by POPULATION, not recency. Recency would let a single straggler
 * reloading an ancient build jump it above the version everyone else is on,
 * which is the opposite of what the screen is for. Ties break on recency, and
 * then on the version string so the order is total and the list never reshuffles
 * between renders.
 */
export function groupByAppVersion(users: AppVersionUser[]): AppVersionGroup[] {
  const buckets = new Map<string, AppVersionUser[]>()

  for (const user of users) {
    const bucket = buckets.get(user.version)
    if (bucket) bucket.push(user)
    else buckets.set(user.version, [user])
  }

  const groups: AppVersionGroup[] = [...buckets].map(([version, bucketed]) => ({
    version,
    /* The query already returns most-recent-first and Map preserves insertion
       order, so each bucket arrives sorted — no per-bucket sort needed. */
    users: bucketed,
    lastSeenAt: bucketed[0]?.seenAt ?? null,
  }))

  return groups.sort(
    (a, b) =>
      b.users.length - a.users.length ||
      (b.lastSeenAt ?? '').localeCompare(a.lastSeenAt ?? '') ||
      a.version.localeCompare(b.version),
  )
}
